import { useBaseUser } from '@hooks/queries/useBaseUser';
import { hallOfEntitiesStore } from '@state/hallOfEntities/hallOfEntities.store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDashboardOrganizationId } from '@utils/dashboardNavigation.utils';
import { AxiosError } from 'axios';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate, useParams } from 'react-router';
import { useUnitSystem } from '../../contexts';
import { BaseApi } from '../../core/api';
import type { OrganizationDTO } from '../../core/models/organization';
import { Actions } from '../../redux/actions';
import type { RootState } from '../../redux/reducers';
import { routePaths } from '../../screens/routes';
import {
  getOrganizationCropCyclePermission,
  getOrganizationWorkspaceDetails,
  getOrganizationsWithPermissions,
  getOrganizationsWithPlans,
} from './organization/organizations.utils';
import { workspaceQueryKey } from './organization/useWorkspace';

const ORG_NOT_FOUND = 'Not found';
const ORG_WITHOUT_LICENSE = 'Org without license';

function isInvalidOrgError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    return status === 403 || status === 404;
  }
  return (
    error instanceof Error &&
    (error.message === ORG_NOT_FOUND || error.message === ORG_WITHOUT_LICENSE)
  );
}

export function useOrganization() {
  const { pathname, state } = useLocation();
  // Checks Org Id when on the Hall: `/app/organizations/workspace/:workspaceId/organization/:organizationId/...`
  const entitiesHallMatchPath = matchPath(
    '/app/organizations/workspace/:workspaceId/organization/:organizationId/*',
    pathname
  );

  const entitiesHallOrgId = entitiesHallMatchPath?.params.organizationId;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [unitData] = useUnitSystem();
  const { organizationId: paramsOrgId } = useParams();
  const { user } = useBaseUser();
  const { viewMode } = useSelector((state: RootState) => state.Map['map']);
  const ldClient = useLDClient();
  const { selectedEntities } = hallOfEntitiesStore();
  const queryClient = useQueryClient();

  const organizationId =
    paramsOrgId ??
    entitiesHallOrgId ??
    getDashboardOrganizationId(pathname, state) ??
    selectedEntities?.organization?.id;

  // eslint-disable-next-line @tanstack/query/no-rest-destructuring, @tanstack/query/exhaustive-deps -- full result re-exposed by design; unitData kept out of the key on purpose (keying on it would refetch + blank the view on unit toggle).
  const { data: organization, ...rest } = useQuery({
    queryKey: ['organization', { orgId: organizationId }],
    queryFn: async () => {
      const { data } = await BaseApi.get<OrganizationDTO>(`/orgs/${organizationId}`, {
        params: { attributes: 'workspaces' },
      });
      const organizations = await getOrganizationsWithPermissions([data]);
      const orgsWithPlans = await getOrganizationsWithPlans(organizations);

      if (!orgsWithPlans.length) {
        throw new Error(ORG_NOT_FOUND);
      }
      const org = orgsWithPlans[0];

      if (!org.licensed) {
        throw new Error(ORG_WITHOUT_LICENSE);
      }

      const [workspaceData, cropCycleWriteAllowed] = await Promise.all([
        getOrganizationWorkspaceDetails(org.workspace_id),
        getOrganizationCropCyclePermission(org),
      ]);

      // Seed useWorkspace rather than let it re-request the workspace we just fetched.
      queryClient.setQueryData(workspaceQueryKey(org.workspace_id), workspaceData);

      return {
        ...org,
        unitSystem: unitData,
        workspace_use_type: workspaceData.use_type,
        permissions: org.permissions.set('crop_cycle:write', cropCycleWriteAllowed),
      };
    },
    enabled: !!organizationId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 0,
    onSettled: (data) => {
      if (data) {
        dispatch(Actions.setUserCurrentWorkspace(data.workspace_id));
        const hasSoilMaps = data.entitlements.has('mapview.soildata');
        if (!hasSoilMaps && viewMode === 'soil_map') {
          dispatch(Actions.setMapViewMode('map'));
        }

        ldClient?.identify({
          kind: 'multi',
          organization: {
            key: data.id,
            name: data.name,
          },
          user: {
            key: user?.id,
            name: user?.name,
            default_workspace: user?.default_workspace_id,
          },
        });
      }
    },
    onError: (error) => {
      if (isInvalidOrgError(error)) {
        navigate(routePaths.organizations);
      }
    },
  });
  return { organization, ...rest };
}
